import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { dmClient } from '../services/dmClient';
import { tokenStore } from './tokenStore';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => tokenStore.getUser());
  const [loading, setLoading] = useState(() => tokenStore.isAuthenticated());

  const refreshMe = useCallback(async () => {
    if (!tokenStore.isAuthenticated()) {
      setUser(null);
      setLoading(false);
      return null;
    }
    try {
      const resp = await dmClient.get('/auth/me');
      const me = resp.data?.user || null;
      setUser(me);
      if (me) tokenStore.setUser(me);
      return me;
    } catch (_e) {
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refreshMe(); }, [refreshMe]);

  const login = useCallback(async (email, password) => {
    const resp = await dmClient.post('/auth/login', { email, password });
    const data = resp.data;
    // Email MFA: no session yet -- caller must collect the code and verify.
    if (data?.mfaRequired) {
      return { mfaRequired: true, mfaToken: data.mfaToken, email };
    }
    if (!data?.success || !data?.token) {
      throw new Error(data?.error || 'Login failed');
    }
    tokenStore.setSession({ token: data.token, refreshToken: data.refreshToken, user: data.user });
    setUser(data.user);
    return data.user;
  }, []);

  const verifyMfa = useCallback(async (mfaToken, code) => {
    const resp = await dmClient.post('/auth/login/verify', { mfaToken, code });
    const data = resp.data;
    if (!data?.success || !data?.token) {
      throw new Error(data?.error || 'Verification failed');
    }
    tokenStore.setSession({ token: data.token, refreshToken: data.refreshToken, user: data.user });
    setUser(data.user);
    return data.user;
  }, []);

  const resendMfa = useCallback(async (mfaToken) => {
    const resp = await dmClient.post('/auth/login/resend', { mfaToken });
    const data = resp.data;
    if (!data?.success) {
      throw new Error(data?.error || 'Could not resend the code');
    }
    return data.mfaToken || mfaToken;
  }, []);

  const register = useCallback(async (payload) => {
    const resp = await dmClient.post('/auth/register', payload);
    if (!resp.data?.success) {
      throw new Error(resp.data?.error || 'Registration failed');
    }
    return resp.data;
  }, []);

  const logout = useCallback(async () => {
    try { await dmClient.post('/auth/logout', {}); } catch (_e) { /* non-fatal */ }
    tokenStore.clear();
    setUser(null);
    window.location.assign('/login');
  }, []);

  const value = useMemo(() => {
    const permissions = new Set(user?.permissions || []);
    const roles = new Set(user?.roles || []);
    return {
      user,
      loading,
      isAuthenticated: !!user,
      isAdmin: roles.has('admin') || permissions.has('admin.manage'),
      hasPermission: (p) => permissions.has(p),
      login,
      verifyMfa,
      resendMfa,
      register,
      logout,
      refreshMe,
      setUser,
    };
  }, [user, loading, login, verifyMfa, resendMfa, register, logout, refreshMe]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
